import { ReactNode } from 'react';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import HtmlContent from '@telialt/common-html-content/src';
import { TextButton } from '@telialt/common-text-button/src';
import { useAskToUnRestrictLeasingModalControls } from '@telialt/core-eshop/src/components/AskToUnRestrictLeasingModal/AskToUnRestrictLeasingModal';
import { useCartQuery } from '@telialt/core-eshop/src/hooks/queries/useCartQuery';
import { LeasingPlanAvailability } from '@telialt/models/src/EshopModel';
import {
    selectIsWithRatePlan,
    selectLeasingPlan,
    selectLeasingPlansAvailability,
    selectPossibleLeasingPlans,
} from '@telialt/utils/src/eshopSelectors';
import { msgReplace, withParams } from '@telialt/utils/src/helpers';
import { TeliaSegment, useSegment } from '@telialt/utils/src/segment';
import { useUserData } from '@telialt/utils/src/SSO/useUserData';
import { useUserId } from '@telialt/utils/src/SSO/useUserId';

type RestrictionType =
    | 'isAnonymousB2CUserWhoNeedsOnlyLeasing'
    | 'isB2BUserWhoNeedsOnlyLeasing'
    | 'leasingPlanAvailabilityNotAvailable'
    | 'leasingPlanAvailabilityLinked';

type RestrictionData = {
    severity: 'info' | 'warning';
    message: ReactNode;
    title: string;
    type: RestrictionType;
};

export const useLeasingRestriction = (): RestrictionData | null => {
    const { t } = useTranslations();
    const { cart } = useCartQuery();
    const { userData } = useUserData();
    const segment = useSegment();
    const userId = useUserId();
    const { openModal } = useAskToUnRestrictLeasingModalControls();

    const isWithRatePlan = selectIsWithRatePlan(cart);
    const isLeasingSelected = Boolean(selectLeasingPlan(cart));
    const leasingPlanAvailability = selectLeasingPlansAvailability(cart);
    const noLeasingPlansAvailable = !selectPossibleLeasingPlans(cart)?.length;

    const isAnonymousB2CUser = userId === 'anonymous' && segment === TeliaSegment.B2c;
    const isAnonymousB2CUserWhoNeedsOnlyLeasing = isAnonymousB2CUser && !isWithRatePlan;
    const isB2BUserWhoNeedsOnlyLeasing = segment === TeliaSegment.B2b && !isWithRatePlan && isLeasingSelected;

    if (noLeasingPlansAvailable) {
        return null;
    }
    if (isAnonymousB2CUserWhoNeedsOnlyLeasing) {
        const text = t['eshop.product_page.leasing_selector.restriction_notification.anonymous_user.message'];

        return {
            type: 'isAnonymousB2CUserWhoNeedsOnlyLeasing',
            severity: 'info',
            title: t['eshop.product_page.leasing_selector.restriction_notification.anonymous_user.title'],
            message: <HtmlContent content={text} />,
        };
    }
    if (isB2BUserWhoNeedsOnlyLeasing) {
        return {
            type: 'isB2BUserWhoNeedsOnlyLeasing',
            severity: 'warning',
            title: t[
                'eshop.product_page.leasing_selector.restriction_notification.b2b_user_only_leasing_payment.title'
            ],
            message: (
                <HtmlContent
                    content={
                        t[
                            'eshop.product_page.leasing_selector.restriction_notification.b2b_user_only_leasing_payment.message'
                        ]
                    }
                />
            ),
        };
    }
    if (leasingPlanAvailability === LeasingPlanAvailability.NotAvailable) {
        return {
            type: 'leasingPlanAvailabilityNotAvailable',
            severity: 'warning',
            title: t['eshop.product_page.leasing_selector.restriction_notification.blacklist.title'],
            message: msgReplace(t['eshop.product_page.leasing_selector.restriction_notification.blacklist.message'], {
                contactLink: (_i, _tag, [text]) => (
                    <TextButton key={text} onClick={() => openModal()}>
                        {text}
                    </TextButton>
                ),
            }),
        };
    }
    if (leasingPlanAvailability === LeasingPlanAvailability.Linked) {
        return {
            type: 'leasingPlanAvailabilityLinked',
            severity: 'warning',
            title: t['eshop.product_page.leasing_selector.restriction_notification.not_contract_owner.title'],
            message: (
                <HtmlContent
                    content={withParams(
                        t['eshop.product_page.leasing_selector.restriction_notification.not_contract_owner.message'],
                        {
                            fullName: userData.fullName,
                        },
                    )}
                />
            ),
        };
    }

    return null;
};
